import React, { useEffect, useState } from "react"
import { formatPhoneNumber } from "react-phone-number-input"
import { connect, useDispatch } from "react-redux"
import { useNavigate } from "react-router"

import { RESET_STORE } from "client/services/constants"
import { Button } from "components/Button"
import { Buttons, Card } from "components/Card/Card"
import { FormPage } from "components/Form/Form"
import useOurForm from "components/Form/useForm"
import { CancelSignupModal } from "components/Modal/Modal"
import { HOME_PAGE, PROFILE_PAGE, REGISTER_PAGE } from "core/constants"
import { languages } from "lib/languages"
import { Action, ActionWithoutPayload, Message } from "types/redux"
import { GlobalStore } from "types/store"

import actions from "./services/actions"
import styles from "./VerifyPhone.module.scss"

interface VerifyPhoneProps {
  loading?: boolean
  success?: boolean
  message?: Message
  phone: string
  editingPhone?: boolean
  canChangePhone: boolean
  verify: (payload: { code: string; phone: string }) => void
  cancel: () => void
  sendAgain: () => void
  editPhone: (editing: boolean) => void
  changePhone: (phone: string) => void
}

const VerifyPhone: React.FC<VerifyPhoneProps> = (props) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const formRef = useOurForm()
  const [cancelModalVisible, setCancelModalVisible] = useState(false)

  useEffect(() => {
    if (props.success) {
      dispatch(actions.reset())
      navigate(PROFILE_PAGE)
    }
  }, [props.success])

  const cancelSignup = (redirectPath: string) => {
    props.cancel()
    dispatch({ type: RESET_STORE })
    navigate(redirectPath)
  }

  const modifyPhone = () => {
    setCancelModalVisible(false)
    // The signup payload is lost on reload: restart the signup instead
    if (props.canChangePhone) props.editPhone(true)
    else cancelSignup(REGISTER_PAGE)
  }

  if (props.editingPhone) {
    return (
      <Card message={props.message}>
        <FormPage
          key="phone"
          inputs={[{ name: "phone", required: true }]}
          prospect={{ phone: props.phone }}
          acceptText={languages.validate}
          loading={props.loading}
          onFinish={(values) => props.changePhone(values.phone)}
          onCancel={() => props.editPhone(false)}
        />
      </Card>
    )
  }

  const label = (
    <div>
      {languages.writeVerifyCode}
      <span className={styles.Phone}>{formatPhoneNumber(props.phone)}</span>
    </div>
  )
  const inputsObj = [{ name: "code", required: true, label, autoSubmit: true }]
  return (
    <Card message={props.message}>
      <FormPage
        formRef={formRef}
        inputs={inputsObj}
        acceptText={languages.register}
        cancelText={languages.cancel}
        loading={props.loading}
        onFinish={(values) => {
          const code = (values as any).code
          props.verify({
            ...(values as { code: string }),
            phone: props.phone,
          })
          formRef.reset(["code"])
        }}
        onCancel={() => setCancelModalVisible(true)}
      />
      <Buttons style={{ paddingTop: 20 }}>
        <Button type="link" size="small" onClick={() => props.sendAgain()}>
          Renvoyer le code
        </Button>
      </Buttons>
      <CancelSignupModal
        visible={cancelModalVisible}
        onCancelSignup={() => cancelSignup(HOME_PAGE)}
        onModifyPhone={modifyPhone}
        onClose={() => setCancelModalVisible(false)}
      />
    </Card>
  )
}
const s = ({
  client,
  verifyPhone,
}: GlobalStore): Omit<VerifyPhoneProps, "cancel" | "verify" | "sendAgain" | "editPhone" | "changePhone"> => {
  const { signupPayload, ...state } = verifyPhone
  return {
    ...state,
    phone: client?.customer?.phone as string,
    canChangePhone: !!signupPayload,
  }
}

const p = (
  dispatch: (a: Action<unknown> | ActionWithoutPayload) => void
): Omit<VerifyPhoneProps, "phone" | "canChangePhone"> => ({
  verify: (payload: { code: string; phone: string }) =>
    dispatch(actions.request(payload)),
  cancel: () => dispatch(actions.cancelRequest()),
  sendAgain: () => dispatch(actions.sendAgainRequest()),
  editPhone: (editing: boolean) => dispatch(actions.editPhone(editing)),
  changePhone: (phone: string) => dispatch(actions.changePhoneRequest(phone)),
})

export default connect(s, p)(VerifyPhone)
